import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { submitLead } from "../lib/api";
import { LOAN_TYPES } from "../lib/data";

export const LeadForm = ({ defaults = {}, onSuccess, compact = false }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    loan_type: defaults.loan_type || "",
    amount: "",
    message: "",
    source: defaults.source || "lead-form",
    ref: localStorage.getItem("ld_ref") || "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setLoading(true);
    try {
      await submitLead(form);
      setDone(true);
      toast.success("Request submitted! Our expert will call you shortly.");
      onSuccess && onSuccess();
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp or call us.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8 px-4" data-testid="lead-form-success">
        <CheckCircle2 className="w-14 h-14 text-approval mx-auto mb-4" />
        <h3 className="font-heading text-xl font-bold text-white">Thank you, {form.name.split(" ")[0]}!</h3>
        <p className="text-slate-400 mt-2 text-sm">
          Your request is received. A Loan Doctor expert will reach out within a few hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="lead-form">
      <div className={compact ? "space-y-3.5" : "grid sm:grid-cols-2 gap-3.5"}>
        <Input
          data-testid="lead-name-input"
          placeholder="Full Name *"
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          className="bg-obsidian-900/60 border-white/10 h-11 text-white placeholder:text-slate-500"
        />
        <Input
          data-testid="lead-phone-input"
          placeholder="Mobile Number *"
          value={form.phone}
          onChange={(e) => set("phone", e.target.value)}
          className="bg-obsidian-900/60 border-white/10 h-11 text-white placeholder:text-slate-500"
        />
      </div>
      <div className={compact ? "space-y-3.5" : "grid sm:grid-cols-2 gap-3.5"}>
        <Input
          data-testid="lead-email-input"
          placeholder="Email (optional)"
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
          className="bg-obsidian-900/60 border-white/10 h-11 text-white placeholder:text-slate-500"
        />
        <Input
          data-testid="lead-city-input"
          placeholder="City"
          value={form.city}
          onChange={(e) => set("city", e.target.value)}
          className="bg-obsidian-900/60 border-white/10 h-11 text-white placeholder:text-slate-500"
        />
      </div>
      <div className={compact ? "space-y-3.5" : "grid sm:grid-cols-2 gap-3.5"}>
        <Select value={form.loan_type} onValueChange={(v) => set("loan_type", v)}>
          <SelectTrigger data-testid="lead-loantype-select" className="bg-obsidian-900/60 border-white/10 h-11 text-white">
            <SelectValue placeholder="Select Service" />
          </SelectTrigger>
          <SelectContent className="bg-obsidian-700 border-white/10 text-white">
            {LOAN_TYPES.map((t) => (
              <SelectItem key={t} value={t} className="focus:bg-gold/20 focus:text-white">{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          data-testid="lead-amount-input"
          placeholder="Loan Amount (₹)"
          value={form.amount}
          onChange={(e) => set("amount", e.target.value)}
          className="bg-obsidian-900/60 border-white/10 h-11 text-white placeholder:text-slate-500"
        />
      </div>
      <Textarea
        data-testid="lead-message-input"
        placeholder="Tell us briefly about your requirement..."
        value={form.message}
        onChange={(e) => set("message", e.target.value)}
        className="bg-obsidian-900/60 border-white/10 text-white placeholder:text-slate-500 min-h-[80px]"
      />
      <Button
        type="submit"
        disabled={loading}
        data-testid="lead-submit-button"
        className="w-full gold-gradient text-obsidian-900 font-bold h-12 text-base rounded-full hover:opacity-90"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Get Free Consultation"}
      </Button>
      <p className="text-center text-xs text-slate-500">
        🔒 Your details are 100% confidential. No spam, ever.
      </p>
    </form>
  );
};
